import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { api, type RegionTreeProvince } from '../api/client.js'
import { friendlyError } from '../lib/friendlyError.js'
import { ChevronDownIcon, ChevronRightIcon, PinIcon } from './icons/GlyphIcons.js'
import { usePopoverAnchor } from './usePopoverAnchor.js'

// RegionFilterPopover — chip-style trigger that opens a 2-column cascade
// (province → city) sourced from `GET /api/regions/tree`. Selecting a city
// or a province emits that single region name through `onChange`, matching
// the existing `?region=<name>` URL contract that DiscoverPage filters on.
//
// Replaces the standalone "Region" view (RegionTree) so that region filtering
// composes with activity / status / sort instead of replacing the list. See
// `wiki/entities/web-discover-page.md` §region-filter for context.

interface Props {
  /** Current `?region=<name>` value. Empty string means "all regions". */
  value: string
  onChange: (next: string) => void
}

export function RegionFilterPopover({ value, onChange }: Props) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [provinces, setProvinces] = useState<RegionTreeProvince[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [activeProvinceName, setActiveProvinceName] = useState<string | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const selectedLabel = value || t('discover.region.placeholder')
  const anchor = usePopoverAnchor(triggerRef, open)

  useEffect(() => {
    if (!open || provinces || loadError) return
    let cancelled = false
    api
      .getRegionTree()
      .then((tree) => {
        if (cancelled) return
        setProvinces(tree.provinces)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(friendlyError(err, t))
      })
    return () => {
      cancelled = true
    }
  }, [loadError, open, provinces, t])

  const activeProvince = useMemo(() => {
    if (!provinces) return null
    if (activeProvinceName) {
      const match = provinces.find((p) => p.name === activeProvinceName)
      if (match) return match
    }
    if (value) {
      const matchByValue = provinces.find(
        (p) => p.name === value || p.cities.some((c) => c.name === value),
      )
      if (matchByValue) return matchByValue
    }
    return provinces[0] ?? null
  }, [activeProvinceName, provinces, value])

  // Outside-click + Escape close
  useEffect(() => {
    if (!open) return
    function onDocClick(event: MouseEvent) {
      const target = event.target as Node | null
      if (!target) return
      if (panelRef.current?.contains(target)) return
      if (triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function commit(next: string) {
    onChange(next)
    setOpen(false)
  }

  const active = value.length > 0
  const triggerCls = active
    ? 'inline-flex h-7 flex-none shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)] px-3 text-xs font-medium text-[var(--xr-brand-primary)]'
    : 'inline-flex h-7 flex-none shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 text-xs font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]'

  return (
    <div className="relative inline-block" data-testid="discover-region-popover">
      <button
        ref={triggerRef}
        type="button"
        className={triggerCls}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="discover-region-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <PinIcon />
        <span className="max-w-32 truncate">{selectedLabel}</span>
        <ChevronDownIcon width={10} height={10} />
      </button>
      {open &&
        anchor &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={t('discover.region.dialogLabel')}
            data-testid="discover-region-panel"
            style={{ position: 'fixed', top: anchor.top, left: anchor.left }}
            className="z-30 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] shadow-[var(--xr-shadow-md)]"
          >
            {loadError && <p className="px-3 py-3 text-xs text-[var(--xr-danger)]">{loadError}</p>}
            {!loadError && !provinces && (
              <p className="px-3 py-3 text-xs text-[var(--xr-text-muted)]">
                {t('discover.region.loading')}
              </p>
            )}
            {provinces && provinces.length === 0 && (
              <p className="px-3 py-3 text-xs text-[var(--xr-text-muted)]">
                {t('discover.region.empty')}
              </p>
            )}
            {provinces && provinces.length > 0 && (
              <div className="grid grid-cols-[130px_1fr]">
                <ul className="max-h-60 overflow-y-auto border-r border-[var(--xr-border-default)] py-1">
                  <li>
                    <button
                      type="button"
                      onClick={() => commit('')}
                      data-testid="discover-region-all"
                      className={
                        'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs ' +
                        (value === ''
                          ? 'bg-[var(--xr-brand-primary-soft)] font-semibold text-[var(--xr-brand-primary)]'
                          : 'text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]')
                      }
                    >
                      {t('discover.region.allRegions')}
                    </button>
                  </li>
                  {provinces.map((province) => {
                    const isActive = province === activeProvince
                    const isSelected = value === province.name
                    return (
                      <li key={province.name}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveProvinceName(province.name)}
                          onFocus={() => setActiveProvinceName(province.name)}
                          onClick={() => setActiveProvinceName(province.name)}
                          data-testid={`discover-region-province-${province.name}`}
                          className={
                            'flex w-full items-center justify-between gap-1 px-3 py-1.5 text-left text-xs ' +
                            (isSelected
                              ? 'bg-[var(--xr-brand-primary-soft)] font-semibold text-[var(--xr-brand-primary)]'
                              : isActive
                                ? 'bg-[var(--xr-bg-surface-elev)] text-[var(--xr-text-primary)]'
                                : 'text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]')
                          }
                        >
                          <span className="truncate">{province.name}</span>
                          <ChevronRightIcon width={9} height={9} />
                        </button>
                      </li>
                    )
                  })}
                </ul>
                <ul className="max-h-60 overflow-y-auto py-1">
                  {activeProvince && (
                    <li>
                      <button
                        type="button"
                        onClick={() => commit(activeProvince.name)}
                        data-testid={`discover-region-province-commit-${activeProvince.name}`}
                        className={
                          'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs ' +
                          (value === activeProvince.name
                            ? 'bg-[var(--xr-brand-primary-soft)] font-semibold text-[var(--xr-brand-primary)]'
                            : 'text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]')
                        }
                      >
                        <span>{t('discover.region.wholeProvince')}</span>
                        <span className="text-[10px] text-[var(--xr-text-muted)]">
                          {activeProvince.count}
                        </span>
                      </button>
                    </li>
                  )}
                  {activeProvince?.cities.map((city) => {
                    const isSelected = value === city.name
                    return (
                      <li key={city.name}>
                        <button
                          type="button"
                          onClick={() => commit(city.name)}
                          data-testid={`discover-region-city-${city.name}`}
                          className={
                            'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs ' +
                            (isSelected
                              ? 'bg-[var(--xr-brand-primary-soft)] font-semibold text-[var(--xr-brand-primary)]'
                              : 'text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]')
                          }
                        >
                          <span className="truncate">{city.name}</span>
                          <span className="text-[10px] text-[var(--xr-text-muted)]">
                            {city.count}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}
